import { object, ref, string } from "yup";

import SspDataHelper from "../accountSignup/sspDataHelper";
import { RegexPatterns } from "../dynamicForm/validators";
import RegistrationDataHelper from "../registrationDialog/registrationDataHelper";

const profileValidationSchema = (intl, isEditing) => {
  const requiredMessage = intl.formatMessage({ id: "form.required" });
  const onlyAlphabeticMessage = intl.formatMessage({ id: "form.error.only_alphabetic_chars" });
  const passwordNotEqual = intl.formatMessage({ id: "registration.password_not_equal" });
  const passwordValidationMessage = intl.formatMessage({ id: "registration.password_validation" });
  const invalidValue = intl.formatMessage({ id: "form.error.invalid_value" });
  const emailMessage = intl.formatMessage({ id: "form.error.invalid_email" });
  const requirements = SspDataHelper.getPasswordRequirements(intl);

  switch (process.env.GATSBY_SITE) {
    case "de":
    case "gb":
      return object().shape({
        alternatePhone: string().matches(
          /^[\d\s\-+/]{5,25}$/,
          intl.formatMessage({ id: "form.error.phone_pattern" }),
        ).when(["mobile"], {
          is: (mobile) => !mobile,
          then: (schema) => (
            schema.required(intl.formatMessage({ id: "form.error.one_phone_number" }))
          ),
        }),
        apartment: string()
          .max(100, intl.formatMessage(
            { id: "form.error.max_length_exact" },
            {
              value: 100,
            },
          ))
          .matches(
            RegistrationDataHelper.noneEmojiRegex,
            invalidValue,
          )
          .optional(),
        city: string()
          .required(requiredMessage)
          .min(2, requiredMessage)
          .max(50, intl.formatMessage(
            { id: "form.error.max_length_exact" },
            {
              value: 50,
            },
          ))
          .matches(
            RegistrationDataHelper.noneEmojiRegex,
            invalidValue,
          ),
        co: string()
          .max(100, intl.formatMessage(
            { id: "form.error.max_length_exact" },
            {
              value: 100,
            },
          ))
          .matches(
            RegistrationDataHelper.noneEmojiRegex,
            invalidValue,
          )
          .optional(),
        confirmPassword: string()
          .when([], {
            is: () => isEditing && isEditing.includes("account"),
            then: (schema) => schema
              .required(requiredMessage) // <-- nur wenn account bearbeitet wird
              .oneOf([ref("password")], passwordNotEqual)
              .matches(RegistrationDataHelper.noneEmojiRegex, invalidValue),
          }),
        email: string()
          .required(requiredMessage)
          .max(100, intl.formatMessage(
            { id: "form.error.max_length_exact" },
            {
              value: 100,
            },
          ))
          .email(emailMessage)
          .matches(RegexPatterns.email, emailMessage),
        firstname: string()
          .min(2, requiredMessage)
          .required(requiredMessage)
          .max(40, intl.formatMessage(
            { id: "form.error.max_length_exact" },
            {
              value: 40,
            },
          ))
          .matches(
            RegistrationDataHelper.noneEmojiRegex,
            onlyAlphabeticMessage,
          ),
        gender: string(),
        houseno: string()
          .required(requiredMessage)
          .max(10, intl.formatMessage(
            { id: "form.error.max_length_exact" },
            {
              value: 10,
            },
          ))
          .matches(
            /^[0-9]+[a-zA-Z0-9 \-/]*$/,
            invalidValue,
          ),
        lastname: string()
          .min(2, requiredMessage)
          .required(requiredMessage)
          .max(100, intl.formatMessage(
            { id: "form.error.max_length_exact" },
            {
              value: 100,
            },
          ))
          .matches(
            RegistrationDataHelper.noneEmojiRegex,
            onlyAlphabeticMessage,
          ),
        mobile: string().matches(
          /^[\d\s\-+/]{5,25}$/,
          intl.formatMessage({ id: "form.error.phone_pattern" }),
        ).when(["alternatePhone"], {
          is: (alternatePhone) => !alternatePhone,
          then: (schema) => (
            schema.required(intl.formatMessage({ id: "form.error.one_phone_number" }))
          ),
        }),
        password: string()
          .when([], {
            is: () => isEditing && isEditing.includes("account"),
            then: (schema) => schema
              .required(requiredMessage)
              .oneOf([ref("confirmPassword")], passwordNotEqual)
              .test("requirements", passwordValidationMessage, (value) => (
                typeof requirements.find((requirement) => (
                  !RegExp(requirement.regex).test(value)
                )) === "undefined"
              ))
              .matches(RegistrationDataHelper.noneEmojiRegex, invalidValue),
          }),
        street: string()
          .required(requiredMessage)
          .min(2, requiredMessage)
          .max(50, intl.formatMessage(
            { id: "form.error.max_length_exact" },
            {
              value: 50,
            },
          ))
          .matches(
            RegistrationDataHelper.noneEmojiRegex,
            invalidValue,
          ),
        title: string().oneOf([
          "Prof. Dr.",
          "Prof.",
          "Dr.",
          "Dr. med.",
          "Dr. med. dent.",
          "Dr. pharm.",
          "Dr. rer. hum."], invalidValue),
        zipcode: string()
          .required(requiredMessage)
          .max(5, intl.formatMessage(
            { id: "form.error.max_length_exact" },
            {
              value: 5,
            },
          ))
          .matches(
            /^[0-9]{5}$/,
            intl.formatMessage({ id: "form.error.zipcode" }),
          ),
      }, [["mobile", "alternatePhone"]]);
    default: return null;
  }
};

export default profileValidationSchema;
